/**
 * One early, shared install-prompt listener: Chromium may fire before the
 * authenticated workspace finishes its initial preload.
 */
export interface DeferredInstallPrompt extends Event {
  prompt: () => Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const INSTALL_STATE_EVENT = 'invoiceflow:pwa-install-state';

let deferredPrompt: DeferredInstallPrompt | null = null;
let installedThisSession = false;
let listening = false;

export const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export const isIPad = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPad/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export const isIOSSafari = (): boolean => {
  if (!isIOSDevice()) return false;
  return /Safari/i.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(navigator.userAgent);
};

export const isPwaInstalled = (): boolean => {
  if (typeof window === 'undefined') return false;
  return installedThisSession ||
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
};

const publishInstallState = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(INSTALL_STATE_EVENT));
};

export const initializePwaInstallListener = (): void => {
  if (typeof window === 'undefined' || listening) return;
  listening = true;

  window.addEventListener('beforeinstallprompt', event => {
    if (isPwaInstalled()) return;
    event.preventDefault();
    deferredPrompt = event as DeferredInstallPrompt;
    publishInstallState();
  });

  window.addEventListener('appinstalled', () => {
    installedThisSession = true;
    deferredPrompt = null;
    publishInstallState();
  });
};

export const hasNativeInstallPrompt = (): boolean => Boolean(deferredPrompt);

export const launchNativeInstallPrompt = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
  const prompt = deferredPrompt;
  if (!prompt) return 'unavailable';
  deferredPrompt = null;
  publishInstallState();
  // Must be invoked synchronously from a real user click, before awaiting.
  try {
    const choice = await prompt.prompt();
    if (choice.outcome === 'accepted') installedThisSession = true;
    publishInstallState();
    return choice.outcome;
  } catch {
    publishInstallState();
    return 'unavailable';
  }
};

initializePwaInstallListener();